import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  ArrowRight, User, Briefcase, FileCheck, Landmark,
  Eye, Headset, ListChecks, ShieldCheck, Scale, ChevronDown,
} from "lucide-react";
import Marquee from "../components/Marquee";
import QuickActions from "../components/QuickActions";
import { Reveal, LineReveal } from "../components/Reveal";
import CTASection from "../components/CTASection";
import { IMAGES, COMPLIANCE_TEXT } from "../lib/siteConfig";

const STATS = [
  { v: "4-Step", l: "Guided Process" },
  { v: "Pan-India", l: "Customer Assistance" },
  { v: "100%", l: "Transparent Communication" },
  { v: "Dedicated", l: "Support Team" },
];

const SERVICES = [
  { id: "personal", icon: User, title: "Personal Loan Assistance", desc: "Guidance on eligibility, documentation and suitable personal loan options from banks and NBFCs." },
  { id: "business", icon: Briefcase, title: "Business Loan Assistance", desc: "Support for working capital, MSME and business expansion funding requirements." },
  { id: "application", icon: FileCheck, title: "Loan Application Support", desc: "End-to-end assistance with application forms, document checklists and follow-ups." },
  { id: "connect", icon: Landmark, title: "Bank & Institution Connect", desc: "We connect eligible customers with banks and financial institutions suited to their profile." },
];

const STEPS = [
  { n: "01", title: "Submit Your Enquiry", desc: "Share your basic details and loan requirement through our simple online enquiry form." },
  { n: "02", title: "Get Personalised Assistance", desc: "Our team reviews your requirement and connects with you to understand your profile and goals." },
  { n: "03", title: "Explore Suitable Loan Options", desc: "We help you explore and compare suitable loan options offered by relevant banks and financial institutions." },
  { n: "04", title: "Apply Through the Institution", desc: "Complete your application directly with the chosen bank or NBFC, with our coordination support." },
];

const WHY = [
  { icon: Eye, title: "Transparency First", desc: "Clear communication, honest guidance and no hidden charges — you always know where your application stands." },
  { icon: Headset, title: "Dedicated Customer Support", desc: "A responsive support team that stays with you from enquiry to application." },
  { icon: ListChecks, title: "Simple, Guided Process", desc: "We simplify documentation and coordination so your loan journey feels effortless." },
  { icon: ShieldCheck, title: "Professional Assistance", desc: "Trained professionals who coordinate with banks and financial institutions on your behalf." },
];

export default function Home() {
  const heroRef = useRef(null);
  const { scrollYProgress } = useScroll({ target: heroRef, offset: ["start start", "end start"] });
  const bgY = useTransform(scrollYProgress, [0, 1], ["0%", "18%"]);
  const bgScale = useTransform(scrollYProgress, [0, 1], [1, 1.12]);

  return (
    <main>
      {/* HERO */}
      <section ref={heroRef} data-testid="home-hero" className="relative min-h-screen flex items-center overflow-hidden">
        <motion.div style={{ y: bgY, scale: bgScale }} className="absolute inset-0">
          <img src={IMAGES.hero} alt="" className="w-full h-full object-cover opacity-30" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A0A0D]/60 via-[#0A0A0D]/80 to-[#0A0A0D]" />
        </motion.div>
        <div
          className="absolute top-1/4 left-[8%] w-[420px] h-[420px] rounded-full pointer-events-none"
          style={{ background: "radial-gradient(circle, rgba(212,175,55,0.14) 0%, transparent 65%)" }}
        />

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-24 w-full">
          <motion.p
            className="caption-gold mb-7 flex items-center gap-3"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.6, delay: 0.1 }}
          >
            <span className="inline-block w-8 h-px bg-[#D4AF37]" />
            Loan Assistance & Facilitation · India
          </motion.p>

          <h1 className="font-display text-4xl sm:text-5xl lg:text-7xl font-bold tracking-tight text-white leading-[1.1] max-w-4xl">
            <LineReveal delay={0.15}>Your Trusted Partner</LineReveal>
            <LineReveal delay={0.3}>
              for <span className="gold-text gold-shimmer">Loan Assistance</span>
            </LineReveal>
          </h1>

          <motion.p
            className="mt-8 max-w-2xl text-base sm:text-lg text-[#A1A1AA] leading-relaxed"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.65 }}
          >
            CREDNOVA SERVICES PRIVATE LIMITED helps individuals and businesses across India connect
            with banks and financial institutions for suitable loan products — with transparent,
            responsible guidance at every step.
          </motion.p>

          <motion.div
            className="mt-10 flex flex-col sm:flex-row gap-4"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.8 }}
          >
            <Link to="/contact" data-testid="hero-cta-get-loan-assistance" className="btn-gold inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm">
              <span className="relative z-10 flex items-center gap-2">
                Get Loan Assistance <ArrowRight size={16} />
              </span>
            </Link>
            <Link to="/contact" data-testid="hero-cta-contact-us" className="btn-outline-gold inline-flex items-center justify-center gap-2 rounded-full px-8 py-4 text-sm">
              Contact Us
            </Link>
          </motion.div>

          <QuickActions />

          <motion.div
            className="mt-16 grid grid-cols-2 md:grid-cols-4 gap-px bg-[rgba(212,175,55,0.15)] border border-[rgba(212,175,55,0.15)] rounded-2xl overflow-hidden"
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            data-testid="hero-stats"
          >
            {STATS.map((s) => (
              <div key={s.l} className="bg-[#0C0C10]/90 px-6 py-6">
                <p className="font-display text-xl sm:text-2xl font-semibold gold-text">{s.v}</p>
                <p className="text-xs text-[#71717A] mt-1.5 tracking-wide">{s.l}</p>
              </div>
            ))}
          </motion.div>
        </div>

        <motion.div
          className="absolute bottom-6 left-1/2 -translate-x-1/2 text-[#71717A]"
          animate={{ y: [0, 8, 0] }}
          transition={{ duration: 2, repeat: Infinity, ease: "easeInOut" }}
        >
          <ChevronDown size={20} />
        </motion.div>
      </section>

      <Marquee />

      {/* SERVICES PREVIEW */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="home-services">
        <Reveal>
          <p className="caption-gold mb-4">What We Do</p>
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6 mb-14">
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-xl">
              Assistance Across Every Stage of Your Loan Journey
            </h2>
            <Link to="/services" data-testid="home-services-view-all" className="text-sm text-[#E6CA65] hover:text-[#F3E5AB] inline-flex items-center gap-2 transition-colors">
              View all services <ArrowRight size={15} />
            </Link>
          </div>
        </Reveal>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s, i) => (
            <Reveal key={s.id} delay={i * 0.1}>
              <Link to="/services" data-testid={`service-card-${s.id}`} className="card-gold block p-7 h-full group">
                <s.icon size={26} className="text-[#D4AF37] mb-6" strokeWidth={1.5} />
                <h3 className="font-display text-lg font-medium text-white mb-3 group-hover:text-[#E6CA65] transition-colors">{s.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{s.desc}</p>
                <span className="inline-flex items-center gap-2 text-xs text-[#D4AF37] mt-6 tracking-wide">
                  Learn more <ArrowRight size={13} className="transition-transform group-hover:translate-x-1" />
                </span>
              </Link>
            </Reveal>
          ))}
        </div>
      </section>

      {/* HOW IT WORKS */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 bg-[#0C0C10] border-y border-[rgba(212,175,55,0.12)]" data-testid="home-how-it-works">
        <div className="max-w-7xl mx-auto">
          <Reveal className="mb-14">
            <p className="caption-gold mb-4">The Process</p>
            <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white">
              How It Works
            </h2>
          </Reveal>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-8">
            {STEPS.map((s, i) => (
              <Reveal key={s.n} delay={i * 0.12}>
                <div data-testid={`process-step-item-${i + 1}`} className="relative pt-6 border-t border-[rgba(212,175,55,0.25)]">
                  <span className="font-display text-6xl font-bold ghost-number absolute -top-3 right-0 select-none">{s.n}</span>
                  <p className="font-mono-brand text-xs text-[#D4AF37] tracking-[0.25em] mb-4">STEP {s.n}</p>
                  <h3 className="font-display text-lg font-medium text-white mb-3">{s.title}</h3>
                  <p className="text-sm text-[#A1A1AA] leading-relaxed">{s.desc}</p>
                </div>
              </Reveal>
            ))}
          </div>
          <Reveal delay={0.2} className="mt-12">
            <Link to="/how-it-works" data-testid="home-how-it-works-link" className="text-sm text-[#E6CA65] hover:text-[#F3E5AB] inline-flex items-center gap-2 transition-colors">
              See the full process <ArrowRight size={15} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* WHY CHOOSE US BENTO */}
      <section className="py-20 lg:py-28 px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="home-why-choose-us">
        <Reveal className="mb-14">
          <p className="caption-gold mb-4">Why Choose Us</p>
          <h2 className="font-display text-2xl sm:text-3xl lg:text-4xl font-semibold tracking-tight text-white max-w-2xl">
            Built on Trust. Driven by Clarity.
          </h2>
        </Reveal>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-5">
          <Reveal className="md:row-span-2">
            <div className="card-gold h-full min-h-[320px] relative overflow-hidden flex flex-col justify-end p-8">
              <img src={IMAGES.handshake} alt="Professional consultation" className="absolute inset-0 w-full h-full object-cover opacity-40" />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0D] via-[#0A0A0D]/55 to-transparent" />
              <div className="relative">
                <p className="caption-gold mb-3">Our Commitment</p>
                <h3 className="font-display text-xl font-medium text-white mb-2">Responsible Guidance, Always</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">
                  We focus on transparent communication and a smooth application experience — never on unrealistic promises.
                </p>
              </div>
            </div>
          </Reveal>
          {WHY.map((w, i) => (
            <Reveal key={w.title} delay={0.08 + i * 0.08}>
              <div data-testid={`why-card-${i + 1}`} className="card-gold p-7 h-full">
                <w.icon size={24} className="text-[#D4AF37] mb-5" strokeWidth={1.5} />
                <h3 className="font-display text-lg font-medium text-white mb-2">{w.title}</h3>
                <p className="text-sm text-[#A1A1AA] leading-relaxed">{w.desc}</p>
              </div>
            </Reveal>
          ))}
        </div>
      </section>

      {/* COMPLIANCE BANNER */}
      <section className="px-4 sm:px-6 lg:px-8 max-w-7xl mx-auto" data-testid="home-compliance-banner">
        <Reveal>
          <div className="flex items-start gap-4 rounded-2xl border border-[rgba(212,175,55,0.25)] bg-[#121216] p-6 sm:p-8">
            <Scale size={22} className="text-[#D4AF37] mt-0.5 shrink-0" strokeWidth={1.5} />
            <div>
              <p className="caption-gold mb-2">Important Notice</p>
              <p className="text-sm text-[#A1A1AA] leading-relaxed">{COMPLIANCE_TEXT}</p>
            </div>
          </div>
        </Reveal>
      </section>

      <CTASection />
    </main>
  );
}
